import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup, Tooltip, useMap } from 'react-leaflet';
import L from 'leaflet';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { MapPin, Users, Activity, Clock, Map as MapIcon, Store, ExternalLink, Shield } from 'lucide-react';
import { useBusiness } from '../../contexts/BusinessContext';
import { usePermissions } from '../../contexts/PermissionsContext';
import api from '../../services/api';
import { getApiErrorMessage } from '../../utils/apiErrors';
import { toast } from 'sonner';
import 'leaflet/dist/leaflet.css';

// Fix Leaflet default icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// Custom blinking marker icons
const createBlinkingIcon = (color) => {
  return L.divIcon({
    className: 'custom-marker',
    html: `
      <div style="
        width: 24px;
        height: 24px;
        background-color: ${color};
        border: 2px solid white;
        border-radius: 50%;
        animation: blink 1.5s infinite;
      "></div>
      <style>
        @keyframes blink {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.3; }
        }
      </style>
    `,
    iconSize: [24, 24],
    iconAnchor: [12, 12]
  });
};

const createSteadyIcon = (color) => {
  return L.divIcon({
    className: 'custom-marker',
    html: `
      <div style="
        width: 24px;
        height: 24px;
        background-color: ${color};
        border: 2px solid white;
        border-radius: 50%;
      "></div>
    `,
    iconSize: [24, 24],
    iconAnchor: [12, 12]
  });
};

// Custom map center component
function MapCenter({ center }) {
  const map = useMap();
  
  useEffect(() => {
    if (center) {
      map.setView(center, 13);
    }
  }, [center, map]);
  
  return null;
}

const LiveTrackingPageEnhanced = () => {
  const { activeBusiness } = useBusiness();
  const { isOwner, isStaff, loading: permissionsLoading } = usePermissions();
  const navigate = useNavigate();
  const { businessId } = useParams();
  
  // ══════════════════════════════════════════════════════════════════════
  // STAFF RESTRICTION: Staff cannot see Staff Map - only Retailer Map
  // ══════════════════════════════════════════════════════════════════════
  
  // Mode toggle: 'STAFF' or 'RETAILERS'
  // Default to RETAILERS mode - will be updated based on role when permissions load
  const [mapMode, setMapMode] = useState('RETAILERS');
  
  // Set correct mode when permissions finish loading
  useEffect(() => {
    if (!permissionsLoading) {
      // Staff users are forced to RETAILERS mode only
      if (isStaff) {
        setMapMode('RETAILERS');
      } else if (isOwner) {
        // Owners default to STAFF map mode
        setMapMode('STAFF');
      }
    }
  }, [permissionsLoading, isStaff, isOwner]);
  
  // Staff mode state
  const [staffData, setStaffData] = useState([]);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [statusFilter, setStatusFilter] = useState(''); // IDLE, OFFLINE, ON_VISIT, ONLINE
  
  // Retailers mode state
  const [retailersData, setRetailersData] = useState([]);
  const [selectedRetailer, setSelectedRetailer] = useState(null);
  const [cityFilter, setCityFilter] = useState('');
  const [retailerStatusFilter, setRetailerStatusFilter] = useState('ALL');
  const [cities, setCities] = useState([]);
  
  // Common state
  const [loading, setLoading] = useState(true);
  const [mapCenter, setMapCenter] = useState([28.6139, 77.2090]); // Default: Delhi
  
  const fetchLiveTracking = useCallback(async () => {
    try {
      const { data } = await api.get('/part3/tracking/live');
      setStaffData(data.staff || []);
      if (data.staff && data.staff.length > 0 && data.staff[0].last_location) {
        setMapCenter([data.staff[0].last_location.lat, data.staff[0].last_location.lng]);
      }
      setLoading(false);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to load live tracking'));
      setLoading(false);
    }
  }, [activeBusiness]);
  
  const fetchRetailersMap = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (cityFilter) params.city = cityFilter;
      if (retailerStatusFilter !== 'ALL') params.collection_status = retailerStatusFilter;
      const { data } = await api.get('/part5/retailers/map', { params });
      setRetailersData(data.retailers || []);
      if (data.retailers && data.retailers.length > 0) {
        const firstRetailer = data.retailers[0];
        if (firstRetailer.latitude && firstRetailer.longitude) {
          setMapCenter([firstRetailer.latitude, firstRetailer.longitude]);
        }
      }
      setLoading(false);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to load retailers map'));
      setLoading(false);
    }
  }, [activeBusiness, cityFilter, retailerStatusFilter]);
  
  const fetchCities = useCallback(async () => {
    try {
      const { data } = await api.get('/part5/assignments/cities');
      setCities(data.cities || []);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to load cities'));
    }
  }, [activeBusiness]);
  
  useEffect(() => {
    // Cities are owner/admin only -- skip for staff to avoid 403 toast
    if (!permissionsLoading && !isStaff) {
      fetchCities();
    }

    if (mapMode === 'STAFF') {
      fetchLiveTracking();
      const interval = setInterval(fetchLiveTracking, 30000);
      return () => clearInterval(interval);
    } else if (mapMode === 'RETAILERS') {
      fetchRetailersMap();
    }
  }, [mapMode, fetchLiveTracking, fetchRetailersMap, fetchCities, permissionsLoading, isStaff]);
  
  const focusOnStaff = (staff) => {
    setSelectedStaff(staff);
    if (staff.last_location) {
      setMapCenter([staff.last_location.lat, staff.last_location.lng]);
    }
  };
  
  const focusOnRetailer = (retailer) => {
    setSelectedRetailer(retailer);
    if (retailer.latitude && retailer.longitude) {
      setMapCenter([retailer.latitude, retailer.longitude]);
    }
  };
  
  const openRetailerInGoogleMaps = (lat, lng) => {
    window.open(`https://www.google.com/maps?q=${lat},${lng}`, '_blank');
  };
  
  const viewRetailerProfile = (retailerId) => {
    navigate(`/business/${businessId}/tracking/retailers/${retailerId}/profile`);
  };
  
  const getStaffMarkerIcon = (staff) => {
    // Enhanced status-based marker logic
    const status = staff.status;
    const isIdle = staff.is_idle;
    const isCollecting = staff.is_collecting;
    
    switch(status) {
      case 'ON_VISIT':
        // Blue blinking when on visit, green blinking if collecting
        return createBlinkingIcon(isCollecting ? '#10b981' : '#3b82f6');
      case 'IDLE':
        // Yellow blinking for idle
        return createBlinkingIcon('#eab308');
      case 'OFFLINE':
        // Red steady for offline
        return createSteadyIcon('#ef4444');
      case 'ONLINE':
        // Green steady for online/active
        return createSteadyIcon('#10b981');
      default:
        // Gray for unknown
        return createSteadyIcon('#9ca3af');
    }
  };
  
  const getRetailerMarkerIcon = (retailer) => {
    const status = retailer.collection_status;
    if (status === 'UNPAID') {
      return createBlinkingIcon('#ef4444'); // Blinking red
    } else if (status === 'PARTIAL') {
      return createSteadyIcon('#f59e0b'); // Steady orange
    } else {
      return createSteadyIcon('#10b981'); // Steady green
    }
  };
  
  const getStatusColor = (status) => {
    switch(status) {
      case 'ONLINE': return 'bg-green-500';
      case 'ON_VISIT': return 'bg-blue-500';
      case 'OFFLINE': return 'bg-gray-400';
      default: return 'bg-gray-300';
    }
  };
  
  const getStatusText = (status) => {
    switch(status) {
      case 'ONLINE': return 'Online';
      case 'ON_VISIT': return 'On Visit';
      case 'OFFLINE': return 'Offline';
      default: return 'No Data';
    }
  };
  
  const filteredStaffData = staffData.filter((staff) => {
    if (statusFilter && staff.status !== statusFilter) {
      return false;
    }
    return true;
  });

  const handleViewCollections = (staff) => {
    navigate(`/business/${businessId}/tracking/staff/${staff.staff_user_id}/collections`);
  };

  const onlineCount = staffData.filter(s => s.status === 'ONLINE').length;
  const onVisitCount = staffData.filter(s => s.status === 'ON_VISIT').length;
  const offlineCount = staffData.filter(s => s.status === 'OFFLINE').length;

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center bg-[#F7F8FA]">
        <div className="w-10 h-10 border-3 border-slate-200 border-t-teal-600 rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-[#F7F8FA]">
      {/* Page Header */}
      <div className="flex-shrink-0 bg-[#F7F8FA] px-6 pt-5 pb-4">
        <div className="max-w-[1600px] mx-auto">
          {/* Title Row */}
          <div className="flex items-start justify-between mb-5">
            <div>
              <h1 className="text-xl font-semibold text-slate-800 tracking-tight">Live Tracking</h1>
              <p className="text-[13px] text-slate-500 mt-0.5 font-normal">Real-time staff and retailer location monitoring</p>
            </div>
          </div>

          {/* Stats Pills */}
          <div className="flex items-center gap-3 mb-5">
            {/* Staff pills -- hidden from staff users (they can't see other staff) */}
            {!isStaff && (
              <>
                <div className="inline-flex items-center gap-2.5 px-3.5 py-2 bg-white rounded-lg shadow-sm">
                  <div className="w-7 h-7 rounded-md bg-slate-50 flex items-center justify-center">
                    <Users className="w-3.5 h-3.5 text-slate-500" />
                  </div>
                  <div>
                    <p className="text-[10px] text-slate-400 uppercase tracking-wider font-medium">Total Staff</p>
                    <p className="text-[14px] font-semibold text-slate-700">{staffData.length}</p>
                  </div>
                </div>

                <div className="inline-flex items-center gap-2.5 px-3.5 py-2 bg-white rounded-lg shadow-sm">
                  <div className="w-7 h-7 rounded-md bg-emerald-50 flex items-center justify-center">
                    <Activity className="w-3.5 h-3.5 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-[10px] text-slate-400 uppercase tracking-wider font-medium">Online</p>
                    <p className="text-[14px] font-semibold text-emerald-600">{onlineCount}</p>
                  </div>
                </div>

                <div className="inline-flex items-center gap-2.5 px-3.5 py-2 bg-white rounded-lg shadow-sm">
                  <div className="w-7 h-7 rounded-md bg-blue-50 flex items-center justify-center">
                    <MapPin className="w-3.5 h-3.5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-[10px] text-slate-400 uppercase tracking-wider font-medium">On Visit</p>
                    <p className="text-[14px] font-semibold text-blue-600">{onVisitCount}</p>
                  </div>
                </div>

                <div className="inline-flex items-center gap-2.5 px-3.5 py-2 bg-white rounded-lg shadow-sm">
                  <div className="w-7 h-7 rounded-md bg-slate-100 flex items-center justify-center">
                    <Clock className="w-3.5 h-3.5 text-slate-500" />
                  </div>
                  <div>
                    <p className="text-[10px] text-slate-400 uppercase tracking-wider font-medium">Offline</p>
                    <p className="text-[14px] font-semibold text-slate-600">{offlineCount}</p>
                  </div>
                </div>
              </>
            )}

            <div className="inline-flex items-center gap-2.5 px-3.5 py-2 bg-white rounded-lg shadow-sm">
              <div className="w-7 h-7 rounded-md bg-purple-50 flex items-center justify-center">
                <Store className="w-3.5 h-3.5 text-purple-600" />
              </div>
              <div>
                <p className="text-[10px] text-slate-400 uppercase tracking-wider font-medium">Retailers</p>
                <p className="text-[14px] font-semibold text-purple-600">{retailersData.length}</p>
              </div>
            </div>
          </div>

          {/* Mode Toggle and Filters */}
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-1 bg-white p-1 rounded-lg shadow-sm">
              {/* Staff Map button - HIDDEN from staff users (also hidden during permissions loading) */}
              {!permissionsLoading && !isStaff && (
                <button
                  onClick={() => setMapMode('STAFF')}
                  className={`px-4 py-1.5 rounded-md text-[13px] font-medium transition-all flex items-center gap-2 ${
                    mapMode === 'STAFF' 
                      ? 'bg-teal-600 text-white' 
                      : 'text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  <Users className="w-4 h-4" />
                  Staff Map
                </button>
              )}
              <button
                onClick={() => setMapMode('RETAILERS')}
                className={`px-4 py-1.5 rounded-md text-[13px] font-medium transition-all flex items-center gap-2 ${
                  mapMode === 'RETAILERS' 
                    ? 'bg-teal-600 text-white' 
                    : 'text-slate-600 hover:bg-slate-50'
                }`}
              >
                <Store className="w-4 h-4" />
                {isStaff ? 'My Retailers' : 'Retailers Map'}
              </button>
            </div>

            {mapMode === 'STAFF' ? (
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-1 bg-white p-1 rounded-lg shadow-sm">
                  <button
                    onClick={() => setStatusFilter('')}
                    className={`px-3 py-1.5 rounded-md text-[12px] font-medium transition-all ${
                      statusFilter === '' ? 'bg-slate-800 text-white' : 'text-slate-600 hover:bg-slate-50'
                    }`}
                  >
                    All
                  </button>
                  <button
                    onClick={() => setStatusFilter('ONLINE')}
                    className={`px-3 py-1.5 rounded-md text-[12px] font-medium transition-all flex items-center gap-1 ${
                      statusFilter === 'ONLINE' ? 'bg-emerald-600 text-white' : 'text-slate-600 hover:bg-slate-50'
                    }`}
                  >
                    <span className="w-2 h-2 bg-emerald-500 rounded-full"></span> Online
                  </button>
                  <button
                    onClick={() => setStatusFilter('ON_VISIT')}
                    className={`px-3 py-1.5 rounded-md text-[12px] font-medium transition-all flex items-center gap-1 ${
                      statusFilter === 'ON_VISIT' ? 'bg-blue-600 text-white' : 'text-slate-600 hover:bg-slate-50'
                    }`}
                  >
                    <span className="w-2 h-2 bg-blue-500 rounded-full"></span> On Visit
                  </button>
                  <button
                    onClick={() => setStatusFilter('IDLE')}
                    className={`px-3 py-1.5 rounded-md text-[12px] font-medium transition-all flex items-center gap-1 ${
                      statusFilter === 'IDLE' ? 'bg-amber-600 text-white' : 'text-slate-600 hover:bg-slate-50'
                    }`}
                  >
                    <span className="w-2 h-2 bg-amber-500 rounded-full"></span> Idle
                  </button>
                  <button
                    onClick={() => setStatusFilter('OFFLINE')}
                    className={`px-3 py-1.5 rounded-md text-[12px] font-medium transition-all flex items-center gap-1 ${
                      statusFilter === 'OFFLINE' ? 'bg-slate-600 text-white' : 'text-slate-600 hover:bg-slate-50'
                    }`}
                  >
                    <span className="w-2 h-2 bg-slate-400 rounded-full"></span> Offline
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <select
                  value={cityFilter}
                  onChange={(e) => setCityFilter(e.target.value)}
                  className="px-3 py-2 text-[13px] bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
                >
                  <option value="">All Cities</option>
                  {cities.map((city) => (
                    <option key={city.city} value={city.city}>
                      {city.city} ({city.retailer_count})
                    </option>
                  ))}
                </select>
                
                <div className="flex items-center gap-1 bg-white p-1 rounded-lg shadow-sm">
                  <button
                    onClick={() => setRetailerStatusFilter('ALL')}
                    className={`px-3 py-1.5 rounded-md text-[12px] font-medium transition-all ${
                      retailerStatusFilter === 'ALL' ? 'bg-slate-800 text-white' : 'text-slate-600 hover:bg-slate-50'
                    }`}
                  >
                    All
                  </button>
                  <button
                    onClick={() => setRetailerStatusFilter('UNPAID')}
                    className={`px-3 py-1.5 rounded-md text-[12px] font-medium transition-all flex items-center gap-1 ${
                      retailerStatusFilter === 'UNPAID' ? 'bg-rose-600 text-white' : 'text-slate-600 hover:bg-slate-50'
                    }`}
                  >
                    <span className="w-2 h-2 bg-rose-500 rounded-full"></span> Unpaid
                  </button>
                  <button
                    onClick={() => setRetailerStatusFilter('PARTIAL')}
                    className={`px-3 py-1.5 rounded-md text-[12px] font-medium transition-all flex items-center gap-1 ${
                      retailerStatusFilter === 'PARTIAL' ? 'bg-amber-600 text-white' : 'text-slate-600 hover:bg-slate-50'
                    }`}
                  >
                    <span className="w-2 h-2 bg-amber-500 rounded-full"></span> Partial
                  </button>
                  <button
                    onClick={() => setRetailerStatusFilter('CLEAR')}
                    className={`px-3 py-1.5 rounded-md text-[12px] font-medium transition-all flex items-center gap-1 ${
                      retailerStatusFilter === 'CLEAR' ? 'bg-emerald-600 text-white' : 'text-slate-600 hover:bg-slate-50'
                    }`}
                  >
                    <span className="w-2 h-2 bg-emerald-500 rounded-full"></span> Clear
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Map and Sidebar Area */}
      <div className="flex-1 overflow-hidden px-6 pb-6">
        <div className="max-w-[1600px] mx-auto h-full">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 h-full">
            {/* Map */}
            <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
              <div className="h-full min-h-[500px]">
                <MapContainer
                  center={mapCenter}
                  zoom={13}
                  style={{ height: '100%', width: '100%' }}
                >
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  <MapCenter center={mapCenter} />
                  
                  {mapMode === 'STAFF' ? (
                    // Staff markers
                    filteredStaffData.map((staff, index) => (
                      staff.last_location && (
                        <Marker
                          key={index}
                          position={[staff.last_location.lat, staff.last_location.lng]}
                          icon={getStaffMarkerIcon(staff)}
                        >
                          <Tooltip permanent direction="top" offset={[0, -14]} className="leaflet-staff-label">
                            {staff.name}
                          </Tooltip>
                          <Popup>
                            <div className="p-2">
                              <div className="font-semibold">{staff.name}</div>
                              <div className="text-sm text-gray-600">Status: {getStatusText(staff.status)}</div>
                              <div className="text-sm text-gray-600">
                                Last seen: {new Date(staff.last_location.timestamp).toLocaleTimeString()}
                              </div>
                              <div className="text-sm text-gray-600 mt-1">
                                Distance today: {staff.today_stats.distance_km} km
                              </div>
                            </div>
                          </Popup>
                        </Marker>
                      )
                    ))
                  ) : (
                    // Retailer markers
                    retailersData.map((retailer, index) => (
                      retailer.latitude && retailer.longitude && (
                        <Marker
                          key={index}
                          position={[retailer.latitude, retailer.longitude]}
                          icon={getRetailerMarkerIcon(retailer)}
                        >
                          <Tooltip permanent direction="top" offset={[0, -14]} className="leaflet-retailer-label">
                            {retailer.name}
                          </Tooltip>
                          <Popup>
                            <div className="p-2 min-w-[200px]">
                              <div className="font-semibold">{retailer.name}</div>
                              <div className="text-sm text-gray-600">
                                {retailer.city}{retailer.state ? `, ${retailer.state}` : ''}
                              </div>
                              <div className="text-sm font-medium mt-1">
                                Outstanding: ₹{retailer.outstanding_amount?.toLocaleString() || 0}
                              </div>
                              <div className="flex gap-1 mt-2">
                                <button
                                  onClick={() => viewRetailerProfile(retailer.id)}
                                  className="px-2 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600"
                                >
                                  View Profile
                                </button>
                                <button
                                  onClick={() => openRetailerInGoogleMaps(retailer.latitude, retailer.longitude)}
                                  className="px-2 py-1 text-xs bg-green-500 text-white rounded hover:bg-green-600"
                                >
                                  Navigate
                                </button>
                              </div>
                            </div>
                          </Popup>
                        </Marker>
                      )
                    ))
                  )}
                </MapContainer>
              </div>
            </div>
        
            {/* List Sidebar */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-100">
                <h3 className="text-[14px] font-semibold text-slate-800">
                  {mapMode === 'STAFF' ? `Active Staff (${filteredStaffData.length})` : `Retailers (${retailersData.length})`}
                </h3>
              </div>
              <div className="overflow-y-auto max-h-[500px]">
                {mapMode === 'STAFF' ? (
                  filteredStaffData.length === 0 ? (
                    <div className="text-center py-8 text-[13px] text-slate-500">
                      {statusFilter ? 'No staff match the filter' : 'No active staff found'}
                    </div>
                  ) : (
                    <div className="divide-y divide-slate-100">
                      {filteredStaffData.map((staff, index) => (
                        <div
                          key={index}
                          onClick={() => focusOnStaff(staff)}
                          className={`p-4 cursor-pointer hover:bg-slate-50 transition-colors ${
                            selectedStaff?.staff_user_id === staff.staff_user_id ? 'bg-teal-50 border-l-2 border-l-teal-500' : ''
                          }`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1 min-w-0">
                              <div className="text-[13px] font-semibold text-slate-800 truncate">{staff.name}</div>
                              <div className="text-[11px] text-slate-500">{staff.phone}</div>
                            </div>
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${
                              staff.status === 'ONLINE' ? 'bg-emerald-50 text-emerald-700' :
                              staff.status === 'ON_VISIT' ? 'bg-blue-50 text-blue-700' :
                              staff.status === 'IDLE' ? 'bg-amber-50 text-amber-700' :
                              'bg-slate-100 text-slate-600'
                            }`}>
                              <span className={`w-1.5 h-1.5 rounded-full ${
                                staff.status === 'ONLINE' ? 'bg-emerald-500' :
                                staff.status === 'ON_VISIT' ? 'bg-blue-500' :
                                staff.status === 'IDLE' ? 'bg-amber-500' :
                                'bg-slate-400'
                              }`}></span>
                              {getStatusText(staff.status)}
                            </span>
                          </div>
                          
                          {staff.last_location && (
                            <div className="mt-2 text-[11px] text-slate-500 space-y-1">
                              <div className="flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                Last seen: {new Date(staff.last_location.timestamp).toLocaleTimeString()}
                              </div>
                              <div className="flex items-center gap-1">
                                <Activity className="w-3 h-3" />
                                Distance: {staff.today_stats?.distance_km || 0} km
                              </div>
                            </div>
                          )}
                          
                          {staff.collections && (
                            <div className="mt-2 pt-2 border-t border-slate-100">
                              <div className="flex items-center justify-between">
                                <div className="text-[11px]">
                                  <span className="text-slate-500">Collections: </span>
                                  <span className={`font-medium ${
                                    staff.collections.marker_status === 'CLEAR' ? 'text-emerald-600' :
                                    staff.collections.marker_status === 'PARTIAL' ? 'text-amber-600' :
                                    'text-rose-600'
                                  }`}>₹{staff.collections.collected_today?.toLocaleString() || 0}</span>
                                </div>
                                <button
                                  onClick={(e) => { e.stopPropagation(); handleViewCollections(staff); }}
                                  className="text-[11px] text-teal-600 hover:text-teal-700 font-medium"
                                >
                                  View Details →
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )
                ) : (
                  retailersData.length === 0 ? (
                    <div className="text-center py-8 text-[13px] text-slate-500">
                      No retailers found
                    </div>
                  ) : (
                    <div className="divide-y divide-slate-100">
                      {retailersData.slice(0, 50).map((retailer, index) => (
                        <div
                          key={index}
                          onClick={() => focusOnRetailer(retailer)}
                          className={`p-4 cursor-pointer hover:bg-slate-50 transition-colors ${
                            selectedRetailer?.id === retailer.id ? 'bg-teal-50 border-l-2 border-l-teal-500' : ''
                          }`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1 min-w-0">
                              <div className="text-[13px] font-semibold text-slate-800 truncate">{retailer.name}</div>
                              <div className="text-[11px] text-slate-500">{retailer.city || '--'}</div>
                            </div>
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${
                              retailer.collection_status === 'CLEAR' ? 'bg-emerald-50 text-emerald-700' :
                              retailer.collection_status === 'PARTIAL' ? 'bg-amber-50 text-amber-700' :
                              'bg-rose-50 text-rose-700'
                            }`}>
                              {retailer.collection_status || 'Unknown'}
                            </span>
                          </div>
                          <div className="mt-1 text-[11px] text-slate-600">
                            Outstanding: <span className="font-medium">₹{retailer.outstanding_amount?.toLocaleString() || 0}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LiveTrackingPageEnhanced;
